import { stageLabel } from '@/components/ui';
import type { Stage } from '@/components/ui';

import { platformLabel, UA_STATE_OPTIONS } from './infoTabFields';
import type { DiffItem } from './infoTabFields';

// project_edit 요청 payload: { project_id, prefix, common:{바뀐 키}, platforms:{code:{바뀐 키}},
//   before:{ common:{전체}, platforms:{code:{전체}} } }
// before 는 서버가 접수 시점에 읽어 담은 값이다(2026-10 이후 요청). 그 전 요청에는 없다.

type Dict = Record<string, unknown>;

export function asDict(v: unknown): Dict {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Dict) : {};
}

export function rawText(v: unknown): string {
  if (v === null || v === undefined || typeof v === 'object') return '';
  return String(v);
}

// 값 하나를 화면 말로 — 편집 화면 select 에 보이던 라벨과 같게.
export function editValueText(field: string, value: unknown): string {
  const text = rawText(value);
  if (!text) return '(빈 값)';
  if (field === 'stage') return stageLabel(text as Stage);
  if (field === 'state_code') return UA_STATE_OPTIONS.find((o) => o.value === text)?.label ?? text;
  return text;
}

// 공통 Stage 의 이전 값. OS마다 Stage 가 다르면 공통 값이 비어 있으므로 OS별 값을 보인다.
export function commonBeforeText(field: string, beforeCommon: Dict, beforePlatforms: Record<string, Dict>): string {
  if (field !== 'stage' || rawText(beforeCommon.stage)) return editValueText(field, beforeCommon[field]);
  const parts = Object.entries(beforePlatforms)
    .filter(([, values]) => rawText(values.stage))
    .map(([code, values]) => `${platformLabel(code)} ${editValueText('stage', values.stage)}`);
  return parts.length > 0 ? `OS마다 다름(${parts.join(' · ')})` : '(빈 값)';
}

export interface ProjectEditChange extends DiffItem {
  // 플랫폼 변경이면 store code, 공통이면 null.
  code: string | null;
}

export interface ProjectEditDiff {
  hasBefore: boolean;
  beforeCommon: Dict;
  beforePlatforms: Record<string, Dict>;
  changes: ProjectEditChange[];
}

// common/platforms 에 담긴 키를 모두 변경으로 본다 — 편집 화면에 없는 키가 API 로 섞여 와도
// 승인자 화면에서 빠지지 않게 고정 목록으로 거르지 않는다.
export function projectEditDiff(payload: Dict): ProjectEditDiff {
  const before = payload.before && typeof payload.before === 'object' ? asDict(payload.before) : null;
  const beforeCommon = asDict(before?.common);
  const beforePlatforms: Record<string, Dict> = {};
  for (const [code, values] of Object.entries(asDict(before?.platforms))) {
    beforePlatforms[code] = asDict(values);
  }
  const changes: ProjectEditChange[] = [];
  for (const [field, value] of Object.entries(asDict(payload.common))) {
    changes.push({
      scope: '공통',
      code: null,
      field,
      before: before ? commonBeforeText(field, beforeCommon, beforePlatforms) : '기록 없음',
      after: editValueText(field, value),
    });
  }
  for (const [code, values] of Object.entries(asDict(payload.platforms))) {
    const previous = beforePlatforms[code] ?? {};
    for (const [field, value] of Object.entries(asDict(values))) {
      changes.push({
        scope: platformLabel(code),
        code,
        field,
        before: before ? editValueText(field, previous[field]) : '기록 없음',
        after: editValueText(field, value),
      });
    }
  }
  return { hasBefore: before !== null, beforeCommon, beforePlatforms, changes };
}

// 요청함 목록 한 줄 — 첫 변경을 '이전 → 이후'로, 나머지는 개수로.
export function projectEditSummary(payload: Dict): string {
  const { hasBefore, changes } = projectEditDiff(payload);
  if (changes.length === 0) return '필드 변경';
  const first = changes[0];
  const scope = first.code ? `${first.scope} ` : '';
  const head = hasBefore
    ? `${scope}${first.field}: ${first.before} → ${first.after}`
    : `${scope}${first.field}: ${first.after}`;
  return changes.length > 1 ? `${head} 외 ${changes.length - 1}개` : head;
}
